import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { Wallet, Clock, ChevronDown, BarChart3, PieChart, TrendingUp as TrendingUpIcon, Info } from 'lucide-react';
import { DashboardStats, BalanceData, OperatorStats as OperatorStatsType } from '../../services/dashboardService';
import WalletDetailsModal from './WalletDetailsModal';


interface OperatorStatsProps {
  dashboardData: DashboardStats | null;
  balanceData: BalanceData | null;
  operatorStatsData: OperatorStatsType | null;
  loading: boolean;
}

interface ActionStats {
  successful: number;
  failed: number;
  pending: number;
  submitted: number;
  total: number;
}

interface OperatorStats {
  successful: number;
  failed: number;
  pending: number;
  submitted: number;
  deposit?: ActionStats;
  withdrawal?: ActionStats;
}

interface OperatorData {
  name: string;
  gradient: string;
  stats: OperatorStats;
  balance: {
    deposit: number;
    withdrawal: number;
    reservedDeposit: number;
    reservedWithdrawal: number;
    available: number;
    inFlight: number;
    total: number;
    currency: string;
  };
  successRate: number;
  trend: number;
}

const OperatorStats: React.FC<OperatorStatsProps> = ({ 
  dashboardData, 
  balanceData, 
  operatorStatsData,
  loading 
}) => {
  const { t, countryConfig, formatCurrency } = useLocalization();
  const [selectedCurrency, setSelectedCurrency] = useState<string>('CDF');
  const [activeTab, setActiveTab] = useState<'cards' | 'charts'>('cards');
  const [selectedOperator, setSelectedOperator] = useState<OperatorData | null>(null);

  // Configuration avec couleurs plus douces et harmonieuses
  const operatorConfig: { [key: string]: { 
    gradient: string; 
    icon: string;
    trend: number;
    textColor: string;
    color: string;
  } } = {
    'airtel': {
      gradient: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)',
      icon: '/logos/airtel.webp',
      trend: 12,
      textColor: 'text-blue-50',
      color: '#667eea'
    },
    'orange': {
      gradient: 'linear-gradient(135deg, #ed8936 1%, #dd6b20 100%)',
      icon: '/logos/orange.webp',
      trend: 8,
      textColor: 'text-orange-50',
      color: '#ed8936'
    },
    'mpesa': {
      gradient: 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)',
      icon: '/logos/mpesa.webp',
      trend: 15,
      textColor: 'text-green-50',
      color: '#38a169'
    },
    'afrimoney': {
      gradient: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)',
      icon: '/logos/afrimoney.webp',
      trend: 5,
      textColor: 'text-purple-50',
      color: '#9f7aea'
    }
  };

  const getDefaultConfig = () => ({
    gradient: 'linear-gradient(135deg, #718096 0%, #4a5568 100%)',
    icon: '/logos/default.webp',
    trend: 0,
    textColor: 'text-gray-50',
    color: '#718096'
  });

  // Filter by default on CDF
  useEffect(() => {
    const currencies = getAvailableCurrencies();
    if (currencies.includes('CDF')) {
      setSelectedCurrency('CDF');
    }
  }, [operatorStatsData, dashboardData]);

  const getOperatorsFromBackendData = (): OperatorData[] => {
    if (operatorStatsData?.operator_stats && balanceData?.balances) {
      return transformOperatorStatsData();
    }
    
    if (dashboardData?.by_operator && balanceData?.balances) {
      return transformDashboardData();
    }

    return [];
  };

  useEffect(() => {
  console.log('=== DEBUG CURRENCIES ===');
  console.log('Operator Stats Data:', operatorStatsData);
  console.log('Dashboard Data:', dashboardData);
  console.log('Balance Data:', balanceData);
  
  const currencies = getAvailableCurrencies();
  console.log('Available Currencies:', currencies);
  console.log('Selected Currency:', selectedCurrency);
}, [operatorStatsData, dashboardData, balanceData, selectedCurrency]);

  const transformOperatorStatsData = (): OperatorData[] => {
    const operators: OperatorData[] = [];
    const requiredMethods = ['airtel', 'orange', 'mpesa', 'afrimoney'];
    
    // Create maps for quick access - maintenant organisé par action
    const operatorMap = new Map();
    operatorStatsData!.operator_stats.forEach(op => {
      const key = `${op.method.toLowerCase()}_${op.currency}_${op.action}`;
      operatorMap.set(key, op);
    });

    const balanceMap = new Map();
    balanceData!.balances.forEach(bal => {
      const key = `${bal.method.toLowerCase()}_${bal.currency}`;
      balanceMap.set(key, bal);
    });

    requiredMethods.forEach(method => {
      ['CDF', 'USD'].forEach(currency => {
        const key = `${method}_${currency}`;
        const operatorBalance = balanceMap.get(key);

        // Apply currency filter
        if (currency !== selectedCurrency) {
          return;
        }

        // Récupérer les données séparées par action
        const depositKey = `${method}_${currency}_debit`;
        const withdrawalKey = `${method}_${currency}_credit`;
        
        const depositData = operatorMap.get(depositKey);
        const withdrawalData = operatorMap.get(withdrawalKey);

        const depositBalance = operatorBalance?.realtime_deposit || 0;
        const withdrawalBalance = operatorBalance?.realtime_withdrawal || 0;
        const reservedDeposit = operatorBalance?.reserved_deposit || 0;
        const reservedWithdrawal = operatorBalance?.reserved_withdrawal || 0;
        const availableBalance = operatorBalance?.available !== undefined
          ? operatorBalance.available
          : (depositBalance - reservedDeposit) + (withdrawalBalance - reservedWithdrawal);
        const inFlightBalance = operatorBalance?.in_flight !== undefined
          ? operatorBalance.in_flight
          : reservedDeposit + reservedWithdrawal;
        const totalBalance = operatorBalance?.total !== undefined
          ? operatorBalance.total
          : depositBalance + withdrawalBalance;

        const config = operatorConfig[method] || getDefaultConfig();

        // Calculs séparés pour dépôt et retrait
        const depositSuccessful = depositData?.successful || 0;
        const depositFailed = depositData?.failed || 0;
        const depositPending = depositData?.pending || 0;
        const depositSubmitted = depositData?.submitted || 0;
        
        const withdrawalSuccessful = withdrawalData?.successful || 0;
        const withdrawalFailed = withdrawalData?.failed || 0;
        const withdrawalPending = withdrawalData?.pending || 0;
        const withdrawalSubmitted = withdrawalData?.submitted || 0;

        // Totaux combinés pour l'affichage
        const totalSuccessful = depositSuccessful + withdrawalSuccessful;
        const totalFailed = depositFailed + withdrawalFailed;
        const totalPending = depositPending + withdrawalPending;
        const totalSubmitted = depositSubmitted + withdrawalSubmitted;
        
        const totalTransactions = totalSuccessful + totalFailed;
        const successRate = totalTransactions > 0 ? (totalSuccessful / totalTransactions) * 100 : 0;

        // Only create if we have data or if it's the selected currency
        if (totalSuccessful > 0 || totalFailed > 0 || totalPending > 0 || totalSubmitted > 0 || totalBalance > 0 || selectedCurrency === currency) {
          operators.push({
            name: method.charAt(0).toUpperCase() + method.slice(1),
            gradient: config.gradient,
            stats: {
              successful: totalSuccessful,
              failed: totalFailed,
              pending: totalPending,
              submitted: totalSubmitted,
              // Ajout des stats détaillées par action
              deposit: {
                successful: depositSuccessful,
                failed: depositFailed,
                pending: depositPending,
                submitted: depositSubmitted,
                total: depositData?.total_amount || 0
              },
              withdrawal: {
                successful: withdrawalSuccessful,
                failed: withdrawalFailed,
                pending: withdrawalPending,
                submitted: withdrawalSubmitted,
                total: withdrawalData?.total_amount || 0
              }
            },
            balance: {
              deposit: depositBalance,
              withdrawal: withdrawalBalance,
              reservedDeposit,
              reservedWithdrawal,
              available: availableBalance,
              inFlight: inFlightBalance,
              total: totalBalance,
              currency: currency
            },
            successRate: Math.round(successRate),
            trend: config.trend
          });
        }
      });
    });

    return operators;
  };

  const transformDashboardData = (): OperatorData[] => {
    const operators: OperatorData[] = [];
    const requiredMethods = ['airtel', 'orange', 'mpesa', 'afrimoney'];
    
    const operatorMap = new Map();
    dashboardData!.by_operator.forEach(op => {
      const key = `${op.method.toLowerCase()}_${op.currency}`;
      operatorMap.set(key, op);
    });

    const balanceMap = new Map();
    balanceData!.balances.forEach(bal => {
      const key = `${bal.method.toLowerCase()}_${bal.currency}`;
      balanceMap.set(key, bal);
    });

    requiredMethods.forEach(method => {
      ['CDF', 'USD'].forEach(currency => {
        const key = `${method}_${currency}`;
        const operator = operatorMap.get(key);
        const operatorBalance = balanceMap.get(key);

        if (currency !== selectedCurrency) {
          return;
        }

        const depositBalance = operatorBalance?.realtime_deposit || 0;
        const withdrawalBalance = operatorBalance?.realtime_withdrawal || 0;
        const reservedDeposit = operatorBalance?.reserved_deposit || 0;
        const reservedWithdrawal = operatorBalance?.reserved_withdrawal || 0;
        const availableBalance = operatorBalance?.available !== undefined
          ? operatorBalance.available
          : (depositBalance - reservedDeposit) + (withdrawalBalance - reservedWithdrawal);
        const inFlightBalance = operatorBalance?.in_flight !== undefined
          ? operatorBalance.in_flight
          : reservedDeposit + reservedWithdrawal;
        const totalBalance = operatorBalance?.total !== undefined
          ? operatorBalance.total
          : depositBalance + withdrawalBalance;

        const config = operatorConfig[method] || getDefaultConfig();
        const successful = operator?.successful || 0;
        const failed = operator?.failed || 0;
        const pending = operator?.pending || 0;
        const submitted = operator?.submitted || 0;
        
        const totalTransactions = successful + failed;
        const successRate = totalTransactions > 0 ? (successful / totalTransactions) * 100 : 0;

        if (successful > 0 || failed > 0 || pending > 0 || submitted > 0 || totalBalance > 0 || selectedCurrency === currency) {
          operators.push({
            name: method.charAt(0).toUpperCase() + method.slice(1),
            gradient: config.gradient,
            stats: {
              successful,
              failed,
              pending,
              submitted
            },
            balance: {
              deposit: depositBalance,
              withdrawal: withdrawalBalance,
              reservedDeposit,
              reservedWithdrawal,
              available: availableBalance,
              inFlight: inFlightBalance,
              total: totalBalance,
              currency: currency
            },
            successRate: Math.round(successRate),
            trend: config.trend
          });
        }
      });
    });

    return operators;
  };

  const operators = getOperatorsFromBackendData();

  // Calculate chart data
  const getSuccessRateChartData = () => {
    return operators.map(op => ({
      operator: op.name,
      rate: op.successRate,
      color: operatorConfig[op.name.toLowerCase()]?.color || '#718096'
    }));
  };

  const getTransactionVolumeData = () => {
    return operators.map(op => ({
      operator: op.name,
      successful: op.stats.successful,
      failed: op.stats.failed,
      pending: op.stats.pending,
      color: operatorConfig[op.name.toLowerCase()]?.color || '#718096'
    }));
  };

  const getBalanceDistributionData = () => {
    return operators.map(op => ({
      operator: op.name,
      available: op.balance.available,
      inFlight: op.balance.inFlight,
      total: op.balance.total,
      color: operatorConfig[op.name.toLowerCase()]?.color || '#718096'
    }));
  };

  const getAvailableCurrencies = (): string[] => {
    const currencies = new Set<string>();
    
    if (operatorStatsData?.operator_stats) {
      operatorStatsData.operator_stats.forEach(op => {
        currencies.add(op.currency);
      });
    } 
    if (dashboardData?.by_operator) {
      dashboardData.by_operator.forEach(op => {
        currencies.add(op.currency);
      });
    }
      // 3. VÉRIFIE AUSSI BALANCE_DATA (IMPORTANT !)
  if (balanceData?.balances && balanceData.balances.length > 0) {
    balanceData.balances.forEach(bal => {
      if (bal.currency) currencies.add(bal.currency);
    });
  }
    
    return ['ALL', ...Array.from(currencies)];
  };

  const availableCurrencies = getAvailableCurrencies();

  // Simple chart components
  const BarChart = ({ data, title, height = 200 }: { data: any[], title: string, height?: number }) => {
    const maxValue = Math.max(...data.flatMap(d => [d.successful || 0, d.failed || 0, d.rate || 0]));
    
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-600" />
          {title}
        </h3>
        <div className="space-y-3" style={{ height: `${height}px` }}>
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-20 text-xs text-gray-600 font-medium">{item.operator}</div>
              <div className="flex-1">
                <div 
                  className="h-5 rounded-full transition-all duration-300 hover:opacity-80"
                  style={{ 
                    width: `${((item.rate || item.successful) / maxValue) * 100}%`,
                    backgroundColor: item.color
                  }}
                ></div>
              </div>
              <div className="w-12 text-right text-xs font-semibold text-gray-700">
                {item.rate ? `${item.rate}%` : item.successful?.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const StackedBarChart = ({ data, title }: { data: any[], title: string }) => {
    const maxValue = Math.max(...data.map(d => d.successful + d.failed + d.pending));
    
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUpIcon className="w-4 h-4 text-green-600" />
          {title}
        </h3>
        <div className="space-y-3">
          {data.map((item, index) => {
            const total = item.successful + item.failed + item.pending;
            return (
              <div key={index} className="flex items-center gap-3">
                <div className="w-20 text-xs text-gray-600 font-medium">{item.operator}</div>
                <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 float-left transition-all duration-300 hover:opacity-90"
                    style={{ width: `${(item.successful / total) * 100}%` }}
                    title={`Successful: ${item.successful}`}
                  ></div>
                  <div 
                    className="h-full bg-red-500 float-left transition-all duration-300 hover:opacity-90"
                    style={{ width: `${(item.failed / total) * 100}%` }}
                    title={`Failed: ${item.failed}`}
                  ></div>
                  <div 
                    className="h-full bg-yellow-500 float-left transition-all duration-300 hover:opacity-90"
                    style={{ width: `${(item.pending / total) * 100}%` }}
                    title={`Pending: ${item.pending}`}
                  ></div>
                </div>
                <div className="w-16 text-right text-xs font-semibold text-gray-700">
                  {total.toLocaleString()}
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Successful</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Failed</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>Pending</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BalanceChart = ({ data, title }: { data: any[], title: string }) => {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <PieChart className="w-4 h-4 text-purple-600" />
          {title}
        </h3>
        <div className="space-y-4">
          {data.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm text-gray-700 font-medium">{item.operator}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(item.total, selectedCurrency)}
                </div>
                <div className="text-xs text-gray-500">
                  {t('balances.available') || 'Disponible'}: {formatCurrency(item.available, selectedCurrency)}
                  {item.inFlight > 0 && (
                    <> • {t('balances.inFlight') || 'Réservé'}: {formatCurrency(item.inFlight, selectedCurrency)}</>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-6 gap-4">
            <div>
              <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-36"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="bg-gray-100 rounded-xl p-4 h-64"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Enhanced Header with Tabs */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Operators Performance</h1>
          <p className="text-gray-600 text-sm mt-1">
            Real-time analytics and insights
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('cards')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'cards' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setActiveTab('charts')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'charts' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Charts
            </button>
          </div>

          {/* Currency Selector */}
          <div className="relative">
            <select 
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="appearance-none px-3 py-2 pr-8 bg-white border border-gray-300 rounded-lg text-gray-700 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all cursor-pointer shadow-sm"
            >
              {availableCurrencies.map(currency => (
                <option key={currency} value={currency}>
                  {currency === 'CDF' ? `🇨🇩 ${currency}` : `💰 ${currency}`}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>

      {activeTab === 'cards' ? (
        /* Cards View */
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {operators.map((operator, index) => {
              const config = operatorConfig[operator.name.toLowerCase()] || getDefaultConfig();
              
              return (
                <button
                  type="button"
                  key={index}
                  onClick={() => setSelectedOperator(operator)}
                  className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl text-left w-full"
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  <div
                    className="relative p-4 text-white h-full min-h-[280px] flex flex-col shadow-lg"
                    style={{ background: config.gradient }}
                  >
                    {/* Click hint */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-1 text-[10px] text-white/80 bg-black/20 backdrop-blur-sm rounded-full px-2 py-0.5">
                        <Info className="w-3 h-3" />
                        <span>{t('balances.clickForDetails') || 'Détails'}</span>
                      </div>
                    </div>
                    {/* Compact Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-sm">
                          <img 
                            src={config.icon} 
                            alt={operator.name}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-white drop-shadow-sm">{operator.name}</h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-white/80 text-xs">{operator.balance.currency}</span>
                            <div className="w-1 h-1 bg-white/50 rounded-full"></div>
                            <span className="text-white/80 text-xs">{operator.trend}% ↗</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-lg font-black text-white drop-shadow-sm">{operator.successRate}%</div>
                        <div className="text-white/70 text-xs">Success Rate</div>
                      </div>
                    </div>

                    {/* Balance Section */}
                    <div className="mb-4 p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20 shadow-inner flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Wallet className="w-3 h-3 text-white/90" />
                          <span className="text-white/90 font-semibold text-xs">{t('balances.available') || 'Disponible'}</span>
                        </div>
                        <span className="text-base font-bold text-white drop-shadow-sm">
                          {formatCurrency(operator.balance.available, operator.balance.currency)}
                        </span>
                      </div>

                      <div className="space-y-2">
                        {operator.balance.inFlight > 0 && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-amber-200" />
                              <span className="text-white/80 text-xs">{t('balances.inFlight') || 'Réservé'}</span>
                            </div>
                            <span className="text-right font-semibold text-amber-100 text-xs">
                              {formatCurrency(operator.balance.inFlight, operator.balance.currency)}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-1 border-t border-white/10">
                          <span className="text-white/70 text-[11px] uppercase tracking-wide">{t('balances.total') || 'Total'}</span>
                          <span className="text-right font-semibold text-white/90 text-xs">
                            {formatCurrency(operator.balance.total, operator.balance.currency)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Statistics - SECTION EXISTANTE */}
                    <div className="space-y-2">
                      <h4 className="text-white/80 font-semibold text-xs">Transactions</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center p-2 bg-white/10 rounded backdrop-blur-sm border border-white/20">
                          <div className="text-sm font-bold text-green-200 drop-shadow-sm">{operator.stats.successful.toLocaleString()}</div>
                          <div className="text-white/70 text-xs">Successful</div>
                        </div>
                        <div className="text-center p-2 bg-white/10 rounded backdrop-blur-sm border border-white/20">
                          <div className="text-sm font-bold text-red-200 drop-shadow-sm">{operator.stats.failed.toLocaleString()}</div>
                          <div className="text-white/70 text-xs">Failed</div>
                        </div>
                        <div className="text-center p-2 bg-white/10 rounded backdrop-blur-sm border border-white/20">
                          <div className="text-sm font-bold text-yellow-200 drop-shadow-sm">{operator.stats.pending.toLocaleString()}</div>
                          <div className="text-white/70 text-xs">Pending</div>
                        </div>
                        <div className="text-center p-2 bg-white/10 rounded backdrop-blur-sm border border-white/20">
                          <div className="text-sm font-bold text-blue-200 drop-shadow-sm">{operator.stats.submitted.toLocaleString()}</div>
                          <div className="text-white/70 text-xs">Submitted</div>
                        </div>
                      </div>
                    </div>

                    {/* NOUVELLE SECTION - Detailed by Action */}
                    {operator.stats.deposit && operator.stats.withdrawal && (
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <h4 className="text-white/80 font-semibold text-xs mb-2">Detailed by Action</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {/* Dépôts */}
                          <div className="bg-white/5 rounded p-2">
                            <div className="text-green-200 font-semibold mb-1">Deposits</div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Success:</span>
                              <span className="text-green-200 font-semibold">{operator.stats.deposit.successful}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Failed:</span>
                              <span className="text-red-200 font-semibold">{operator.stats.deposit.failed}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Pending:</span>
                              <span className="text-yellow-200 font-semibold">{operator.stats.deposit.pending}</span>
                            </div>
                          </div>
                          {/* Retraits */}
                          <div className="bg-white/5 rounded p-2">
                            <div className="text-blue-200 font-semibold mb-1">Withdrawals</div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Success:</span>
                              <span className="text-green-200 font-semibold">{operator.stats.withdrawal.successful}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Failed:</span>
                              <span className="text-red-200 font-semibold">{operator.stats.withdrawal.failed}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Pending:</span>
                              <span className="text-yellow-200 font-semibold">{operator.stats.withdrawal.pending}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                  </div>

                  {/* Enhanced shadow effect */}
                  <div className="absolute inset-0 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                </button>
              );
            })}
          </div>

        </>
      ) : (
        /* Charts View */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Success Rate Comparison */}
          <BarChart 
            data={getSuccessRateChartData()} 
            title="Success Rate by Operator"
            height={160}
          />

          {/* Transaction Volume */}
          <StackedBarChart 
            data={getTransactionVolumeData()} 
            title="Transaction Volume Distribution"
          />

          {/* Balance Overview */}
          <BalanceChart 
            data={getBalanceDistributionData()} 
            title="Balance Distribution by Operator"
          />

          {/* Performance Summary */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUpIcon className="w-4 h-4 text-blue-600" />
              Performance Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Top Performers</h4>
                {operators
                  .sort((a, b) => b.successRate - a.successRate)
                  .slice(0, 2)
                  .map((op, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">{op.name}</span>
                      <span className="text-sm font-semibold text-green-600">{op.successRate}%</span>
                    </div>
                  ))
                }
              </div>
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Highest Volume</h4>
                {operators
                  .sort((a, b) => (b.stats.successful + b.stats.failed) - (a.stats.successful + a.stats.failed))
                  .slice(0, 2)
                  .map((op, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">{op.name}</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {(op.stats.successful + op.stats.failed).toLocaleString()}
                      </span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No operators message */}
      {operators.length === 0 && !loading && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-sm">
            {selectedCurrency === 'CDF'
              ? "No operators found for CDF"
              : `No operators found for ${selectedCurrency}`
            }
          </div>
        </div>
      )}

      <WalletDetailsModal
        data={
          selectedOperator
            ? (() => {
                const cfg =
                  operatorConfig[selectedOperator.name.toLowerCase()] || getDefaultConfig();
                const b = selectedOperator.balance;
                return {
                  name: selectedOperator.name,
                  currency: b.currency,
                  deposit: b.deposit,
                  withdrawal: b.withdrawal,
                  reservedDeposit: b.reservedDeposit,
                  reservedWithdrawal: b.reservedWithdrawal,
                  available: b.available,
                  inFlight: b.inFlight,
                  total: b.total,
                  gradient: cfg.gradient,
                  iconUrl: cfg.icon,
                };
              })()
            : null
        }
        onClose={() => setSelectedOperator(null)}
      />
    </div>
  );
};

export default OperatorStats;